import React, { useContext, useEffect, useState } from "react";
import { ContentSection, ensure, InfoMessage, ListTable, Stack, SuccessMessage } from "sprint-common-web";

import { DynamicSubtitleType } from "../../../../common/types/DynamicSubtitleType";
import { RecordType } from "../../../../common/types/RecordType";
import { OrderAssetAvailability } from "../../../common/types/OrderAssetAvailability";
import { OrderResolutionDimensions } from "../../../common/types/OrderResolutionDimensions";
import { OrderVideoSelectionSource } from "../../../common/types/OrderVideoSelectionSource";
import { OrderDashboardItem, VideoFileType } from "../OrderDashboardItem";
import { OrderDashboardContext } from "../provider/OrderDashboardContext";
import { AssetsAvailabilityItem, mapToAssetsAvailabilityItems } from "./AssetsAvailabilityItem";
import { createOrderAssetsSectionColumns } from "./createOrderAssetsSectionColumns";

interface OrderAssetsSectionProps {
    height: number;
}

export const OrderAssetsSection: React.VFC<OrderAssetsSectionProps> = ({ height }: OrderAssetsSectionProps) => {
    const { orderDashboardItem, isLoading, areNotesLoading } = useContext(OrderDashboardContext);

    const [assetsAvailabilityItems, setAssetsAvailabilityItems] = useState<AssetsAvailabilityItem[]>([]);

    const video = assetsAvailabilityItems.find((p) => p.type === RecordType.Video);
    const isTrimmed = video && video.fileType === VideoFileType.H265Trimmed;

    useEffect(() => {
        setAssetsAvailabilityItems(mapToAssetsAvailabilityItems(orderDashboardItem));
    }, [orderDashboardItem]);

    const columns = createOrderAssetsSectionColumns({ areNotesLoading });

    const { notAvailableSubs, requiredTypes } = getRequiredTypes({
        isLoading,
        assetsAvailabilityItems,
        orderDashboardItem
    });
    const hasDifferentAspectRatio =
        orderDashboardItem.cloudAssets.video.aspectRatio?.id !==
            orderDashboardItem.encodeSpecification.targetAspectRatio.id &&
        orderDashboardItem.cloudAssets.video.selectionSource === OrderVideoSelectionSource.User;

    const hasDifferentResolution =
        !areResolutionsMatching(
            orderDashboardItem.cloudAssets.video.targetResolution,
            orderDashboardItem.targetResolution
        ) && orderDashboardItem.cloudAssets.video.selectionSource === OrderVideoSelectionSource.User;

    return (
        <ContentSection header="Assets" style={{ height }} data-testId="assets-section">
            {isTrimmed && <SuccessMessage message="Trimmed assets" />}
            {notAvailableSubs && <InfoMessage message={"Required subtitle types: " + requiredTypes} />}
            {(hasDifferentAspectRatio || hasDifferentResolution) && (
                <InfoMessage message="Please note that video used for encoding has been manually selected by end user" />
            )}
            <Stack style={{ overflowY: "auto", height: 250 }}>
                <ListTable
                    columns={columns}
                    items={assetsAvailabilityItems}
                    isLoading={isLoading}
                    testId="cloud-order-assets-list"
                    shimmerLines={1}
                    noItemsMessage="No assets"
                    onRenderRow={(props, defaultRender) => {
                        ensure(props);
                        ensure(defaultRender);

                        const testProps = {
                            "data-testid": "table-row",
                            "data-testkey": props.item.id,
                            "data-testdisabled": props.item.isDisabled
                        };

                        return defaultRender({ ...props, ...testProps, styles: { root: { border: "none" } } });
                    }}
                />
            </Stack>
        </ContentSection>
    );
};

const areResolutionsMatching = (
    resolution?: OrderResolutionDimensions,
    referenceResolution?: OrderResolutionDimensions
) => resolution?.width === referenceResolution?.width && resolution?.height === referenceResolution?.height;

const getRequiredTypes = ({
    isLoading,
    assetsAvailabilityItems,
    orderDashboardItem
}: {
    isLoading: boolean;
    assetsAvailabilityItems: AssetsAvailabilityItem[];
    orderDashboardItem: OrderDashboardItem;
}): { notAvailableSubs?: AssetsAvailabilityItem; requiredTypes: string } => {
    if (isLoading) return { notAvailableSubs: undefined, requiredTypes: "" };

    const notAvailableSubs = assetsAvailabilityItems.find(
        (p) => p.type === RecordType.Subtitle && p.assetInformation.availability !== OrderAssetAvailability.FullMatch
    );
    const typesFromSet = Array.from(new Set([orderDashboardItem.muxSubtitleType, orderDashboardItem.copySubtitleType]));

    const requiredTypes = typesFromSet
        .filter((item): item is DynamicSubtitleType => !!item)
        .map((p) => DynamicSubtitleType[p].toString())
        .join(", ");

    return { notAvailableSubs, requiredTypes };
};
